import * as React from 'react';

import { cn } from './lib/cn';

export interface CheckboxProps {
  checked: boolean | 'indeterminate';
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Unavailable but still focusable: a native `disabled` drops the tab stop, so a keyboard user never reaches the reason. */
  'aria-disabled'?: boolean;
  invalid?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
}

/**
 * A visually hidden native input inside a label, per the Batch-2 sheet's own
 * note that the production implementation may use the shadcn pattern. The
 * probe's `role="checkbox"` span is a drawing; the native input brings form
 * participation, Space-to-toggle and the indeterminate property for free.
 *
 * `onCheckedChange` always reports a boolean. Mixed is a computed summary of
 * children, so a parent derives it — no user gesture produces it, and a
 * callback that could emit it would invite call sites to store a state the user
 * cannot reach.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onCheckedChange,
      disabled = false,
      'aria-disabled': ariaDisabled = false,
      invalid = false,
      id,
      className,
      ...aria
    },
    ref,
  ) => {
    const inner = React.useRef<HTMLInputElement | null>(null);
    const isMixed = checked === 'indeterminate';
    const unavailable = disabled || ariaDisabled;

    React.useImperativeHandle(ref, () => inner.current as HTMLInputElement);

    // `indeterminate` is a DOM property with no attribute, so it cannot be set
    // from JSX — this is the only way to reach it.
    React.useEffect(() => {
      if (inner.current) inner.current.indeterminate = isMixed;
    }, [isMixed]);

    return (
      <span className={cn('relative inline-flex h-4 w-4 shrink-0', className)}>
        <input
          ref={inner}
          id={id}
          type="checkbox"
          checked={checked === true}
          disabled={disabled}
          aria-disabled={ariaDisabled || undefined}
          aria-invalid={invalid || undefined}
          onChange={(event) => {
            if (!ariaDisabled) onCheckedChange(event.target.checked);
          }}
          className={cn(
            'peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed',
            ariaDisabled && 'cursor-not-allowed',
          )}
          {...aria}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none flex h-4 w-4 items-center justify-center rounded-sm border',
            'transition-colors duration-micro ease-out',
            'peer-focus-visible:shadow-focus',
            checked === false
              ? 'border-neutral-300 bg-surface'
              : 'border-interactive bg-interactive text-interactive-on',
            invalid && 'border-danger-500',
            unavailable &&
              (checked === false ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-300 bg-neutral-300'),
          )}
        >
          {isMixed && <span className={cn('h-0.5 w-2 rounded-full bg-current', unavailable && 'bg-neutral-50')} />}
          {checked === true && (
            <svg
              viewBox="0 0 16 16"
              className={cn('h-3 w-3', unavailable && 'text-neutral-50')}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M3.5 8.5l3 3 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      </span>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
